import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Repeat, CheckCircle, XCircle, Search } from "lucide-react";
import { AdminFilterBar } from "@/components/admin/AdminFilterBar";

const statusColors = { active: "bg-green-100 text-green-800", cancelled: "bg-red-100 text-red-800", expired: "bg-slate-100 text-slate-600" };
const targetColors = { COACH: "bg-purple-100 text-purple-800", PROVIDER: "bg-blue-100 text-blue-800", CONDO: "bg-amber-100 text-amber-800", LGU: "bg-green-100 text-green-800" };

export default function AdminSubscriptions() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [targetFilter, setTargetFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filter !== "all") params.status = filter;
    api.get("/admin/subscriptions", { params }).then(r => { setSubs(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [filter]);

  const totalMRR = subs.filter(s => s.status === "active").reduce((sum, s) => sum + (s.price || 0), 0);
  const targetOptions = Array.from(new Set(subs.map((sub) => sub.plan_target).filter(Boolean))).sort();
  const filteredSubs = useMemo(() => subs.filter((sub) => {
    const matchesSearch = !searchQuery || [sub.user_name, sub.user_email, sub.plan_name].filter(Boolean).join(" ").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTarget = targetFilter === "all" || sub.plan_target === targetFilter;
    return matchesSearch && matchesTarget;
  }), [subs, searchQuery, targetFilter]);
  const activeFilters = [
    searchQuery ? { key: "search", label: `Search: ${searchQuery}`, onRemove: () => setSearchQuery("") } : null,
    filter !== "all" ? { key: "status", label: `Status: ${filter}`, onRemove: () => setFilter("all") } : null,
    targetFilter !== "all" ? { key: "target", label: `Type: ${targetFilter}`, onRemove: () => setTargetFilter("all") } : null,
  ].filter(Boolean);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="admin-subscriptions-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight flex items-center gap-3">
            <Repeat className="w-8 h-8 text-court-orange" />Subscriptions
          </h1>
          <p className="text-muted-foreground mt-1">MRR: ₱{totalMRR.toLocaleString()} from {subs.filter(s => s.status === "active").length} active</p>
        </div>
      </div>

      <AdminFilterBar
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search subscriptions by subscriber or plan"
        searchTestId="subscription-search-input"
        filters={[
          { key: "status", label: "Status", value: filter, onChange: setFilter, options: [{ value: "all", label: `All (${subs.length})` }, { value: "active", label: "Active" }, { value: "cancelled", label: "Cancelled" }, { value: "expired", label: "Expired" }], testId: "sub-filter" },
          { key: "target", label: "Type", value: targetFilter, onChange: setTargetFilter, options: [{ value: "all", label: "All Targets" }, ...targetOptions.map((target) => ({ value: target, label: target }))], testId: "subscription-target-filter" },
        ]}
        activeFilters={activeFilters}
        onClearAll={() => { setSearchQuery(""); setFilter("all"); setTargetFilter("all"); }}
        extraActions={<Button variant="outline" className="rounded-md" data-testid="subscription-refine-btn"><Search className="w-4 h-4 mr-2" />Refine</Button>}
      />

      {filteredSubs.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200">
          <Repeat className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="font-heading text-lg text-muted-foreground uppercase">No subscriptions yet</p>
        </div>
      ) : (
        <div className="bg-white border-2 border-slate-100 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-heading uppercase tracking-wider text-xs">Subscriber</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs">Plan</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs">Type</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs">Price</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs">Status</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs">Period</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSubs.map(sub => (
                <TableRow key={sub.subscription_id} data-testid={`sub-row-${sub.subscription_id}`}>
                  <TableCell>
                    <p className="font-medium text-sm">{sub.user_name}</p>
                    <p className="text-[10px] text-muted-foreground">{sub.user_email}</p>
                  </TableCell>
                  <TableCell className="font-medium text-sm">{sub.plan_name}</TableCell>
                  <TableCell>
                    <Badge className={`${targetColors[sub.plan_target] || ''} text-[10px] font-heading uppercase`}>{sub.plan_target}</Badge>
                  </TableCell>
                  <TableCell className="font-heading font-bold text-court-orange">₱{sub.price?.toLocaleString()}</TableCell>
                  <TableCell>
                    <Badge className={`${statusColors[sub.status] || ''} text-[10px] font-heading uppercase`}>
                      {sub.status === "active" ? <CheckCircle className="w-3 h-3 mr-1 inline" /> : <XCircle className="w-3 h-3 mr-1 inline" />}
                      {sub.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{sub.start_date} to {sub.end_date}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
